import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { KycRequirementSlotDto } from '../../services/kycDocuments.service';

/**
 * R1 reuse popup (mixed model, user 2026-08-22): lists the JOURNEY person's vault —
 * available/confirmed documents are applied to the journey on "Reuse & continue"
 * (the consent flow: profile fill + Blob→SharePoint archive + Dataverse sync);
 * missing ones the investor uploads next in Smart Upload. "Upload all fresh" is the
 * all-slots escape hatch (supersede chain keeps the vault clean).
 */
interface KycReuseModalProps {
  show: boolean;
  productName?: string | null;
  slots: KycRequirementSlotDto[];
  busy?: boolean;
  onReuse: () => void;
  onFresh: () => void;
  onClose: () => void;
  /** false when Smart Upload is switched off: "Upload all fresh" would open Smart Upload, so it is hidden. */
  allowFresh?: boolean;
}

const isAvailable = (s: KycRequirementSlotDto) =>
  s.state === 'VALID' || s.state === 'EXPIRES_SOON' || s.state === 'PENDING_REVIEW';

export const KycReuseModal: React.FC<KycReuseModalProps> = ({
  show, productName, slots, busy = false, onReuse, onFresh, onClose, allowFresh = true,
}) => {
  if (!show) return null;

  const available = slots.filter(isAvailable);
  const needed = slots.filter((s) => !isAvailable(s));

  // react-bootstrap Modal (user 2026-08-26): the hand-rolled <div class="modal"> could
  // briefly stack a stale copy of itself during the "Applying…" busy transition (an
  // enabled "Upload all fresh" peeking from underneath). Managed mounting + a single
  // backdrop make that impossible; while busy only "Applying…" remains actionable-looking.
  return (
    <Modal show onHide={busy ? () => undefined : onClose} centered backdrop={busy ? 'static' : true}>
      <Modal.Header closeButton={!busy}>
        <Modal.Title className="fs-5">Documents already available</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted mb-3">
          {`To continue${productName ? ` "${productName}"` : ' this journey'}, you can reuse the KYC
          documents already on file. Your consent is recorded with the date and time.`}
        </p>
        {available.length > 0 && (
          <div className="mb-3">
            <div className="small fw-semibold mb-1">Available — will be applied to this journey:</div>
            {available.map((s) => (
              <div key={s.documentType} className="small text-success">
                <i className="bi bi-check-circle-fill me-1"></i>{s.label}
              </div>
            ))}
          </div>
        )}
        {needed.length > 0 && (
          <div className="mb-1">
            <div className="small fw-semibold mb-1">Still needed — you&apos;ll upload these next:</div>
            {needed.map((s) => (
              <div key={s.documentType} className="small text-muted">
                <i className="bi bi-circle me-1"></i>{s.label}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!busy && allowFresh && (
          <Button variant="outline-secondary" size="sm" onClick={onFresh}>
            Upload all fresh
          </Button>
        )}
        <Button variant="primary" size="sm" onClick={onReuse} disabled={busy}>
          {busy ? 'Applying…' : 'Reuse & continue'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default KycReuseModal;
